import React, { useEffect, useRef, useState } from 'react';

const DEFAULT_PARAMS = {
  build_approach: 'composite_parallel',
  max_iterations_per_chunk: 2,
  chunk_retries: 2,
  model: 'gpt-4o',
  max_words_per_chunk: 250,
  chunking_strategy: 'word_based',
  hard_limit_words: 800,
  enable_coverage_validation: true,
  min_coverage_threshold: 70,
  enable_reextraction: true,
  fge_llm_budget: 150,
  fge_coverage_threshold: 70,
  fge_extract_images: true,
  fge_vision_model: 'gpt-4o',
  fge_enable_xref: true,
  fge_enable_coref: true,
  fge_enable_tables: true,
  fge_enable_vocab_forge: true,
  fge_enable_typer_v2: true,
  fge_enable_domain_ontology: true,
};

const PROVIDER_TYPE_LABELS = {
  openai: 'OpenAI',
  do_serverless: 'DO Serverless',
  do_droplet: 'DO Droplet',
  do_dedicated: 'DO Dedicated',
  runpod_pod: 'GPU Pod',
  runpod_serverless: 'RunPod Serverless',
};

const CHUNKING_STRATEGY_OPTIONS = [
  { value: 'word_based', label: 'Word-Based (Simple)' },
  { value: 'structure_aware', label: 'Structure-Aware (FAQ/Lists)' },
];

const BUILD_APPROACH_OPTIONS = [
  { value: 'composite_sequential', label: 'Composite Approach - Sequential' },
  { value: 'composite_parallel', label: 'Composite Approach - Parallel' },
  { value: 'fast_graph_engine_precision', label: 'FGE (Precision Atomizer + Lossless Coverage)' },
  { value: 'fge_v4', label: 'FGE v4 (Optimized - Parallel + Cached)' },
  { value: 'fge_v5', label: 'FGE v5 (Hierarchy-Aware Clustering)' },
  { value: 'fge_v6', label: 'FGE v6 (Quality-Hardened + Entity Dedup)' },
  { value: 'fge_v7', label: 'FGE v7 (V4 Extraction + V6 Quality Stack)' },
  { value: 'fge_v8', label: 'FGE v8 (V7 + Performance Optimized)' },
  { value: 'fge_v9', label: 'FGE v9 (Verbatim Text + Gate Classification + Predicates)' },
  { value: 'fge_v10', label: 'FGE v10 (V9 + Schema Abstraction Layer)' },
  { value: 'fge_v8_cortex', label: 'FGE v8 Cortex — Intelligence Document' },
  { value: 'excel_graph_builder', label: 'Excel Graph Builder' },
];

const FAST_APPROACHES = new Set([
  'fast_graph_engine_precision', 'fge_v4', 'fge_v5', 'fge_v6', 'fge_v7',
  'fge_v8', 'fge_v9', 'fge_v10', 'fge_v8_cortex', 'excel_graph_builder',
]);

export const DEFAULT_BUILD_SETTINGS = {
  baseName: 'CMS',
  workspaceId: '',
  description: '',
  autoGeneratePrompt: true,
  processingParams: { ...DEFAULT_PARAMS },
};

export function buildSettingsPayload(settings) {
  return {
    baseName: (settings.baseName || '').trim() || 'CMS',
    workspaceId: settings.workspaceId || '',
    description: settings.description || '',
    autoGeneratePrompt: settings.autoGeneratePrompt !== false,
    processingParams: {
      ...settings.processingParams,
      auto_generate_prompt: settings.autoGeneratePrompt !== false,
    },
  };
}

export function summarizeBuildSettings(settings, options) {
  const params = settings.processingParams || {};
  const approach = BUILD_APPROACH_OPTIONS.find((item) => item.value === params.build_approach);
  const providers = options?.providers || [];
  const workspaces = options?.workspaces || [];
  const workspace = workspaces.find((item) => item.workspaceId === settings.workspaceId);
  let model = params.model || 'gpt-4o';
  if (model.startsWith('provider::')) {
    const parts = model.split('::');
    const provider = providers.find((item) => item.id === parts[1]);
    model = provider
      ? `${provider.modelName} (${PROVIDER_TYPE_LABELS[provider.providerType] || provider.providerType}${provider.name ? ` - ${provider.name}` : ''})`
      : (parts[2] || model);
  }
  return {
    baseName: (settings.baseName || '').trim() || 'CMS',
    workspaceName: workspace?.workspaceName || 'the configured workspace',
    approach: approach?.label || params.build_approach || 'Composite Approach - Parallel',
    model,
    autoGeneratePrompt: settings.autoGeneratePrompt !== false,
    description: (settings.description || '').trim(),
  };
}

function modelOptions(providers, current) {
  const options = (providers || []).map((provider) => ({
    value: `provider::${provider.id}::${provider.modelName}`,
    label: `${provider.modelName} (${PROVIDER_TYPE_LABELS[provider.providerType] || provider.providerType}${provider.name ? ` - ${provider.name}` : ''})`,
  }));
  if (current && !options.some((item) => item.value === current)) {
    const parts = String(current).split('::');
    options.unshift({
      value: current,
      label: current.startsWith('provider::') ? (parts[2] || current) : current,
    });
  }
  if (!options.length) options.push({ value: 'gpt-4o', label: 'gpt-4o' });
  return options;
}

export default function LcdNcdBuildSettings({ value, onChange, options }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const seeded = useRef(false);
  const params = value.processingParams || DEFAULT_PARAMS;
  const providers = options?.providers || [];
  const workspaces = options?.workspaces || [];
  const models = modelOptions(providers, params.model);
  const fast = FAST_APPROACHES.has(params.build_approach);

  useEffect(() => {
    if (seeded.current || !options) return;
    const providerList = options.providers || [];
    const workspaceList = options.workspaces || [];
    const fallbackWorkspace = options.defaults?.workspaceId || workspaceList[0]?.workspaceId || '';
    seeded.current = true;
    onChange((prev) => {
      const currentModel = prev.processingParams?.model || '';
      const needsModel = providerList.length > 0 && !currentModel.startsWith('provider::');
      const chosen = providerList.find((item) => item.isDefault) || providerList[0];
      return {
        ...prev,
        workspaceId: prev.workspaceId || fallbackWorkspace,
        processingParams: {
          ...prev.processingParams,
          model: needsModel && chosen
            ? `provider::${chosen.id}::${chosen.modelName}`
            : currentModel,
        },
      };
    });
  }, [options, onChange]);

  function setParams(patch) {
    onChange((prev) => ({
      ...prev,
      processingParams: { ...prev.processingParams, ...patch },
    }));
  }

  return (
    <div className="admin-card lcdncd-settings">
      <h3>Build settings</h3>
      <p className="admin-hint lcdncd-settings__lead">
        These are the same choices as the bulk graph builder. Each selected policy is submitted as its own graph.
      </p>
      <div className="lcdncd-settings__grid">
        <label>Base graph name
          <input
            type="text"
            value={value.baseName}
            onChange={(e) => onChange((prev) => ({ ...prev, baseName: e.target.value }))}
            placeholder='e.g. "CMS" produces "CMS - LCD_33252_V29"'
          />
        </label>
        <label>Workspace
          <select
            value={value.workspaceId}
            onChange={(e) => onChange((prev) => ({ ...prev, workspaceId: e.target.value }))}
          >
            {!workspaces.length && <option value={value.workspaceId || ''}>Configured workspace</option>}
            {workspaces.map((workspace) => (
              <option key={workspace.workspaceId} value={workspace.workspaceId}>{workspace.workspaceName}</option>
            ))}
          </select>
        </label>
        <label className="lcdncd-settings__wide">Graph description (optional)
          <textarea
            rows={2}
            value={value.description}
            onChange={(e) => onChange((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Applied to every graph in this batch. Leave blank to keep the policy id, version, and dates."
          />
        </label>
        <label className="lcdncd-settings__check lcdncd-settings__wide">
          <input
            type="checkbox"
            checked={value.autoGeneratePrompt !== false}
            onChange={(e) => onChange((prev) => ({ ...prev, autoGeneratePrompt: e.target.checked }))}
          />
          Auto-generate custom prompt for each document
        </label>
        <p className="admin-hint lcdncd-settings__wide">Uses AI to create a document-specific extraction prompt before processing.</p>
      </div>

      <h4>Processing settings</h4>
      <div className="lcdncd-settings__grid">
        <label className="lcdncd-settings__wide">Build approach
          <select value={params.build_approach || 'composite_parallel'} onChange={(e) => setParams({ build_approach: e.target.value })}>
            {BUILD_APPROACH_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>
        {fast && (
          <p className="admin-hint lcdncd-settings__wide">
            FGE uses a rule-based pipeline with a bounded LLM atomization budget and a lossless gap-fill pass.
          </p>
        )}
        <label>Model
          <select value={params.model || models[0].value} onChange={(e) => setParams({ model: e.target.value })}>
            {models.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>
        {fast ? (
          <>
            <label>LLM atomization budget
              <input
                type="number"
                min="0"
                max="500"
                step="10"
                value={params.fge_llm_budget ?? 150}
                onChange={(e) => setParams({ fge_llm_budget: parseInt(e.target.value, 10) || 0 })}
              />
            </label>
            <p className="admin-hint lcdncd-settings__wide">
              Max atomization calls per document. 0 is rule-based only. 150–250 is the usual range for policy text.
            </p>
            <button type="button" className="lcdncd-settings__advanced" onClick={() => setShowAdvanced((open) => !open)}>
              Advanced settings {showAdvanced ? '▼' : '▶'}
            </button>
            {showAdvanced && (
              <>
                <label className="lcdncd-settings__check">
                  <input
                    type="checkbox"
                    checked={params.fge_extract_images !== false}
                    onChange={(e) => setParams({ fge_extract_images: e.target.checked })}
                  />
                  Extract images
                </label>
                {params.fge_extract_images !== false && (
                  <label>Vision model
                    <input
                      type="text"
                      value={params.fge_vision_model ?? 'gpt-4o'}
                      onChange={(e) => setParams({ fge_vision_model: e.target.value })}
                    />
                  </label>
                )}
                {[
                  ['fge_enable_xref', 'Resolve cross-references'],
                  ['fge_enable_coref', 'Resolve coreference'],
                  ['fge_enable_tables', 'Extract tables'],
                  ['fge_enable_vocab_forge', 'Vocabulary forge'],
                  ['fge_enable_typer_v2', 'Type resolution v2'],
                  ['fge_enable_domain_ontology', 'Domain ontology classification'],
                ].map(([key, label]) => (
                  <label key={key} className="lcdncd-settings__check">
                    <input
                      type="checkbox"
                      checked={params[key] !== false}
                      onChange={(e) => setParams({ [key]: e.target.checked })}
                    />
                    {label}
                  </label>
                ))}
              </>
            )}
          </>
        ) : (
          <>
            <label>Max iterations per chunk
              <input
                type="number"
                min="1"
                max="5"
                value={params.max_iterations_per_chunk}
                onChange={(e) => setParams({ max_iterations_per_chunk: parseInt(e.target.value, 10) || 2 })}
              />
            </label>
            <label>Chunk retries
              <input
                type="number"
                min="0"
                max="5"
                value={params.chunk_retries}
                onChange={(e) => setParams({ chunk_retries: parseInt(e.target.value, 10) || 2 })}
              />
            </label>
            <label>Max words per chunk
              <input
                type="number"
                min="100"
                max="500"
                step="50"
                value={params.max_words_per_chunk}
                onChange={(e) => setParams({ max_words_per_chunk: parseInt(e.target.value, 10) || 250 })}
              />
            </label>
            <label>Chunking strategy
              <select
                value={params.chunking_strategy || 'word_based'}
                onChange={(e) => setParams({ chunking_strategy: e.target.value })}
              >
                {CHUNKING_STRATEGY_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </label>
            {params.chunking_strategy === 'structure_aware' && (
              <div className="lcdncd-settings__wide">
                <label>Hard limit (words)
                  <input
                    type="number"
                    min="400"
                    max="1500"
                    step="100"
                    value={params.hard_limit_words || 800}
                    disabled={params.hard_limit_words === 0}
                    onChange={(e) => setParams({ hard_limit_words: parseInt(e.target.value, 10) || 800 })}
                  />
                </label>
                <label className="lcdncd-settings__check">
                  <input
                    type="checkbox"
                    checked={params.hard_limit_words === 0}
                    onChange={(e) => setParams({ hard_limit_words: e.target.checked ? 0 : 800 })}
                  />
                  No limit
                </label>
              </div>
            )}
            <p className="lcdncd-settings__section">Coverage validation</p>
            <label className="lcdncd-settings__check">
              <input
                type="checkbox"
                checked={params.enable_coverage_validation !== false}
                onChange={(e) => setParams({ enable_coverage_validation: e.target.checked })}
              />
              Enable coverage validation
            </label>
            <label>Min coverage threshold (%)
              <input
                type="number"
                min="50"
                max="95"
                step="5"
                value={params.min_coverage_threshold || 70}
                disabled={params.enable_coverage_validation === false}
                onChange={(e) => setParams({ min_coverage_threshold: parseInt(e.target.value, 10) || 70 })}
              />
            </label>
            <label className="lcdncd-settings__check">
              <input
                type="checkbox"
                checked={params.enable_reextraction !== false}
                disabled={params.enable_coverage_validation === false}
                onChange={(e) => setParams({ enable_reextraction: e.target.checked })}
              />
              Enable re-extraction
            </label>
          </>
        )}
      </div>
    </div>
  );
}
